/**
 * GitHub API client for fetching repository information.
 */

import axios, { AxiosResponse } from 'axios';

interface GitHubTag {
  name: string;
  date: string;
  sha: string;
  commit: {
    sha: string;
  };
}

interface GitHubCommit {
  sha: string;
  commit: {
    message: string;
    author: {
      name: string;
      date: string;
    };
  };
  html_url: string;
}

interface FormattedCommit {
  sha: string;
  message: string;
  formatted_message: string;
  author: string;
  date: string;
  url: string;
  full_message: string;
  formatted_full_message: string;
}

interface GraphQLResponse {
  data: {
    repository: {
      refs: {
        edges: Array<{
          node: {
            name: string;
            target: {
              oid?: string;
              committedDate?: string;
              target?: {
                oid: string;
                committedDate: string;
              };
            };
          };
        }>;
      };
    };
  };
}

interface CompareResponse {
  commits: GitHubCommit[];
}

export class GitHubClient {
  private token: string | null;
  private baseUrl = 'https://api.github.com';
  private headers: Record<string, string>;
  private ticketSource: string;
  private ticketWorkspace: string;

  constructor(token?: string, ticketSource: string = 'linear', ticketWorkspace: string = '') {
    this.token = token || null;
    this.ticketSource = ticketSource;
    this.ticketWorkspace = ticketWorkspace;
    this.headers = {
      'Accept': 'application/vnd.github.v3+json',
      'User-Agent': 'ticket-tracker',
    };
    if (this.token) {
      this.headers['Authorization'] = `Bearer ${this.token}`;
    }
  }

  private async makeRequest<T>(endpoint: string, params?: Record<string, any>): Promise<T | null> {
    const url = `${this.baseUrl}/${endpoint}`;

    try {
      const response: AxiosResponse<T> = await axios.get(url, {
        headers: this.headers,
        params: params || {},
      });
      return response.data;
    } catch (error) {
      console.error('GitHub API request failed:', error);
      throw error;
    }
  }

  private async graphqlRequest(query: string, variables?: Record<string, any>): Promise<any> {
    const url = `${this.baseUrl}/graphql`;
    const headers = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    };

    const data = { query, variables: variables || {} };

    try {
      const response = await axios.post(url, data, { headers });
      return response.data;
    } catch (error) {
      console.error('GitHub GraphQL request failed:', error);
      throw error;
    }
  }

  async getRepositoryTags(owner: string, repo: string, perPage: number = 100): Promise<GitHubTag[]> {
    const query = `
      {
        repository(owner: "${owner}", name: "${repo}") {
          refs(
            refPrefix: "refs/tags/"
            last: 100
            orderBy: { field: TAG_COMMIT_DATE, direction: ASC }
          ) {
            edges {
              node {
                name
                target {
                  ... on Commit {
                    oid
                    committedDate
                  }
                  ... on Tag {
                    target {
                      ... on Commit {
                        oid
                        committedDate
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const responseData: GraphQLResponse = await this.graphqlRequest(query);

    if (!responseData || !responseData.data) {
      return [];
    }

    const edges = responseData.data.repository?.refs?.edges || [];
    
    return edges
      .map(edge => ({
        name: edge.node.name,
        date: edge.node.target.committedDate ?? edge.node.target.target?.committedDate ?? '',
        sha: edge.node.target.oid ?? edge.node.target.target?.oid ?? '',
        commit: {
          sha: edge.node.target.oid ?? edge.node.target.target?.oid ?? '',
        },
      }))
      .reverse(); // Reverse to get newest first
  }

  async getCommitInfo(owner: string, repo: string, sha: string): Promise<GitHubCommit | null> {
    return this.makeRequest<GitHubCommit>(`repos/${owner}/${repo}/commits/${sha}`);
  }

  async getCommitsBetweenTags(
    owner: string,
    repo: string,
    fromTagSha: string,
    toTagSha: string
  ): Promise<GitHubCommit[]> {
    const endpoint = `repos/${owner}/${repo}/compare/${fromTagSha}...${toTagSha}`;
    const responseData = await this.makeRequest<CompareResponse>(endpoint);

    if (!responseData || !responseData.commits) {
      return [];
    }

    return responseData.commits.reverse();
  }

  formatTicketNumbers(message: string): string {
    // Regex pattern to match common ticket formats (ENG-XXXX, PROJ-123, etc.)
    const pattern = /\b([A-Z]{2,}-\d+)\b/gi;

    return message.replace(pattern, (match, ticketId) => {
      if (!this.ticketWorkspace) {
        // Just highlight the ticket without linking if no workspace is configured
        return `<span style="background-color: #e3f2fd; padding: 2px 4px; border-radius: 3px; color: #1976d2; font-weight: bold;">${ticketId}</span>`;
      }

      // Generate the appropriate URL based on ticket source
      let ticketUrl: string;
      if (this.ticketSource === 'linear') {
        ticketUrl = `https://linear.app/${this.ticketWorkspace.toLowerCase()}/issue/${ticketId}`;
      } else if (this.ticketSource === 'jira') {
        // Handle both domain formats (mycompany.atlassian.net or just mycompany)
        const domain = this.ticketWorkspace.includes('.') ? this.ticketWorkspace : `${this.ticketWorkspace}.atlassian.net`;
        ticketUrl = `https://${domain}/browse/${ticketId}`;
      } else {
        // Fallback to highlight only
        return `<span style="background-color: #e3f2fd; padding: 2px 4px; border-radius: 3px; color: #1976d2; font-weight: bold;">${ticketId}</span>`;
      }

      return `<a href="${ticketUrl}" target="_blank" style="background-color: #e3f2fd; padding: 2px 4px; border-radius: 3px; text-decoration: none; color: #1976d2; font-weight: bold;">${ticketId}</a>`;
    });
  }

  formatCommitForDisplay(commit: GitHubCommit): FormattedCommit {
    const commitData = commit.commit;
    const author = commitData.author;

    const rawMessage = commitData.message;
    const firstLine = rawMessage.split('\n')[0];

    return {
      sha: commit.sha.substring(0, 7), // Short SHA
      message: firstLine, // First line only (raw)
      formatted_message: this.formatTicketNumbers(firstLine), // With ticket highlighting
      author: author.name || 'Unknown',
      date: author.date || '',
      url: commit.html_url || '',
      full_message: rawMessage,
      formatted_full_message: this.formatTicketNumbers(rawMessage),
    };
  }
}
